export { getComputedVariableColor } from "../tt-ui-chart/colors"

export const colorPaletteColorsById: Record<string, string> = {
  "$tt-chart-color-1": "hsl(var(--chart-1))",
  "$tt-chart-color-2": "hsl(var(--chart-2))",
  "$tt-chart-color-3": "hsl(var(--chart-3))",
  "$tt-chart-color-4": "hsl(var(--chart-4))",
  "$tt-chart-color-5": "hsl(var(--chart-5))",
  "$tt-chart-color-6": "hsl(var(--chart-6))",
  "$tt-chart-color-7": "hsl(var(--chart-7))",
  "$tt-chart-color-8": "hsl(var(--chart-8))",
  "$tt-chart-color-9": "hsl(var(--chart-9))",
  "$tt-chart-color-10": "hsl(var(--chart-10))",
  "$tt-chart-color-12": "hsl(var(--chart-12))",
  "$tt-chart-color-13": "hsl(var(--chart-13))",
  "$tt-chart-color-14": "hsl(var(--chart-14))",
  "$tt-chart-color-15": "hsl(var(--chart-15))",
  "$tt-chart-color-16": "hsl(var(--chart-16))",
  "$tt-chart-color-17": "hsl(var(--chart-17))",
  "$tt-chart-color-18": "hsl(var(--chart-18))",
  "$tt-chart-color-19": "hsl(var(--chart-19))",
  "$tt-chart-color-20": "hsl(var(--chart-20))",
  "$tt-chart-color-21": "hsl(var(--chart-21))",
} as const

export const colorValues = Object.values(colorPaletteColorsById)

export const otherColor = "rgba(100,100,100,0.4)"

/** Lookup up of color palette color variables by their ID. The variables are
 * defined within the theme and are light & dark theme aware. */

export type ColorPaletteColorId = keyof typeof colorPaletteColorsById

export const isColorPaletteColorId = (
  id: string | keyof typeof colorPaletteColorsById
): id is keyof typeof colorPaletteColorsById => {
  return id in colorPaletteColorsById
}

/**
 * Returns the color variable for a color palette color by its ID (e.g.
 * `$tt-chart-color-1`). If the ID is not a valid color palette color ID, it
 * returns null. Will automatically change with theme changes.
 */

export const getColorPaletteColorVariableById = (id: string) => {
  if (isColorPaletteColorId(id)) {
    return colorPaletteColorsById[id]
  }

  return undefined
}

const availableColors = Object.keys(colorPaletteColorsById)

export function getAvailableColor(index: number): string {
  return availableColors[index % availableColors.length] ?? otherColor
}

/**
 * Resolve a serie's stamped `color` into a CSS-renderable value for sibling
 * UI (legend swatches, pie slices, tooltips). Palette tokens
 * (`$tt-chart-color-N`) map to their theme-reactive `hsl(var(--chart-N))`
 * form; anything else (explicit config colors, the grey "Other") passes
 * through unchanged. Falls back to the palette color at `fallbackIndex`
 * when the serie carries no color. Sibling components must render this —
 * never re-derive a color from their own row index, which drifts from the
 * drawn chart whenever series order differs from palette order.
 */
export function resolveSerieColor(
  color: string | undefined,
  fallbackIndex: number
): string {
  if (color) {
    return getColorPaletteColorVariableById(color) ?? color
  }
  return colorValues[fallbackIndex % colorValues.length] ?? otherColor
}

interface GetSeriesColorOptions {
  /** Color configuration - either an array or object mapping group IDs to colors */
  colorsConfig?: (string | null)[] | string[] | Record<string, string>
  /** Group identifier used to look up a color in colorsConfig (e.g., "Revenue", "Other") */
  groupId: string
  /** Index for looking up explicit color in colorsConfig array */
  groupIndex: number
  /**
   * Index for selecting from available color palette (defaults to groupIndex).
   * Use this when you need a different index for the fallback color selection,
   * e.g., when offsetting colors across multiple legend groups.
   */
  globalGroupIndex?: number
  /** Whether to resolve color palette IDs to their actual color values (e.g., `$tt-chart-color-1` to `hsl(var(--chart-1))`) */
  resolvePaletteColors?: boolean
}

/**
 * Determines the color for a series based on chart colors config, "Other" category handling,
 * or fallback to available colors.
 *
 * Priority:
 * 1. Explicit color from config (by groupIndex or groupId)
 * 2. otherColor if groupId is "Other" and no explicit color
 * 3. getAvailableColor(globalGroupIndex ?? groupIndex) as fallback
 */
export function getSeriesColor({
  colorsConfig,
  groupId,
  groupIndex,
  globalGroupIndex,
  resolvePaletteColors = false,
}: GetSeriesColorOptions): string {
  // Check for explicit color
  const explicitColor = Array.isArray(colorsConfig)
    ? colorsConfig[groupIndex]
    : colorsConfig?.[groupId]

  let color: string

  if (explicitColor !== undefined) {
    color = explicitColor ?? ""
  } else if (groupId === "Other") {
    color = otherColor
  } else {
    color = getAvailableColor(globalGroupIndex ?? groupIndex)
  }

  // Resolve palette color IDs if requested
  if (resolvePaletteColors && isColorPaletteColorId(color)) {
    return getColorPaletteColorVariableById(color) ?? color
  }

  return color
}
